import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
import { PAYMENT_DIRECTIONS, paymentAccountTypeSchema } from "./payments.js";
/** Expenses, financial transactions and the cashbook (A-02, A-03, PRD §11; docs/specs/accounting.md). */
/** What a money movement was (docs/specs/accounting.md "Sources and kinds"). */
export const FINANCIAL_KINDS = [
    "sale_receipt",
    "refund",
    "other_in",
    "other_out",
    "transfer_in",
    "transfer_out",
    "cod_payout",
    "courier_charge",
    "expense",
    "supplier_payment",
    "customer_payment",
    "cash_in",
    "cash_out",
    "shift_variance",
];
export const financialKindSchema = z.enum(FINANCIAL_KINDS);
/** The longest period a cashbook or report covers in one request (a year and a day for leap years). */
export const MAX_RANGE_DAYS = 366;
const cursorQuery = {
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
};
const dayCount = (from, to) => (Date.parse(to) - Date.parse(from)) / 86_400_000 + 1;
/** `from` ≤ `to`, at most `MAX_RANGE_DAYS` days. */
export const dateRangeSchema = z
    .object({ from: z.iso.date(), to: z.iso.date() })
    .refine((r) => r.from <= r.to, { message: "`from` must not be after `to`", path: ["to"] })
    .refine((r) => dayCount(r.from, r.to) <= MAX_RANGE_DAYS, {
    message: `At most ${String(MAX_RANGE_DAYS)} days`,
    path: ["to"],
});
// Expense categories -------------------------------------------------------------------------------------------------
export const expenseCategorySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    status: z.enum(["active", "archived"]),
    version: z.int(),
    createdAt: z.iso.datetime(),
});
export const expenseCategoryListSchema = z.object({ data: z.array(expenseCategorySchema) });
export const createExpenseCategoryRequestSchema = z
    .object({ name: z.string().trim().min(1).max(80) })
    .strict();
export const updateExpenseCategoryRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    status: z.enum(["active", "archived"]).optional(),
    version: z.int().min(1),
})
    .strict();
// Expenses ---------------------------------------------------------------------------------------------------------
export const expenseSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    categoryId: z.uuid(),
    /** Null: a head-office expense (no branch). */
    locationId: z.uuid().nullable(),
    accountId: z.uuid(),
    /** The `out` payment that took the money from the account (its reversal for a void). */
    paymentId: z.uuid(),
    /** Positive: money spent; negative: the void of `reversalOfId`. */
    amountPaisa: paisa(),
    occurredAt: z.iso.datetime(),
    businessDate: z.iso.date(),
    description: z.string(),
    receiptFileId: z.uuid().nullable(),
    reversalOfId: z.uuid().nullable(),
    /** Set on an expense that has been voided. */
    reversedById: z.uuid().nullable(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const expenseListQuerySchema = z.object({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    locationId: z.uuid().optional(),
    categoryId: z.uuid().optional(),
    accountId: z.uuid().optional(),
    ...cursorQuery,
});
export const expenseListSchema = z.object({ data: z.array(expenseSchema), nextCursor: z.uuid().nullable() });
/** `POST /v1/expenses`: money out of an account for a category (A-02). */
export const recordExpenseRequestSchema = z
    .object({
    categoryId: z.uuid(),
    locationId: z.uuid().nullable().default(null),
    accountId: z.uuid(),
    amountPaisa: paisa().positive(),
    occurredAt: z.iso.datetime({ offset: true }).optional(),
    description: z.string().trim().max(500).default(""),
    receiptFileId: z.uuid().nullable().default(null),
})
    .strict();
export const voidExpenseRequestSchema = z.object({ note: z.string().trim().min(1).max(500) }).strict();
export const expenseEventSchema = expenseSchema.omit({ reversedById: true });
// Financial transactions -------------------------------------------------------------------------------------------
export const financialTransactionSchema = z.object({
    id: z.uuid(),
    kind: financialKindSchema,
    direction: z.enum(PAYMENT_DIRECTIONS),
    amountPaisa: paisa(),
    accountId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    method: z.string().nullable(),
    /** `payment`, `payment_failed`, `supplier_payment`, `credit_entry`, `cash_movement`, `shift`. */
    sourceType: z.string(),
    sourceId: z.uuid(),
    orderId: z.uuid().nullable(),
    customerId: z.uuid().nullable(),
    supplierId: z.uuid().nullable(),
    reference: z.string().nullable(),
    reversalOfId: z.uuid().nullable(),
    occurredAt: z.iso.datetime(),
    businessDate: z.iso.date(),
    note: z.string(),
});
export const financialTransactionListQuerySchema = z.object({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    locationId: z.uuid().optional(),
    accountId: z.uuid().optional(),
    kind: financialKindSchema.optional(),
    sourceType: z.string().trim().min(1).max(40).optional(),
    sourceId: z.uuid().optional(),
    ...cursorQuery,
});
export const financialTransactionListSchema = z.object({
    data: z.array(financialTransactionSchema),
    nextCursor: z.uuid().nullable(),
});
// Cashbook ---------------------------------------------------------------------------------------------------------
/** At most this many entries come back; `truncated` says there were more (day totals stay complete). */
export const CASHBOOK_MAX_ENTRIES = 2000;
export const cashbookQuerySchema = z
    .object({ locationId: z.uuid(), from: z.iso.date(), to: z.iso.date() })
    .and(dateRangeSchema);
export const cashbookSchema = z.object({
    locationId: z.uuid(),
    from: z.iso.date(),
    to: z.iso.date(),
    /** The location's cash accounts (drawers and cash boxes). */
    accounts: z.array(z.object({ id: z.uuid(), name: z.string(), type: paymentAccountTypeSchema })),
    openingPaisa: paisa(),
    closingPaisa: paisa(),
    inPaisa: paisa(),
    outPaisa: paisa(),
    days: z.array(z.object({
        date: z.iso.date(),
        openingPaisa: paisa(),
        inPaisa: paisa(),
        outPaisa: paisa(),
        closingPaisa: paisa(),
    })),
    entries: z.array(financialTransactionSchema.extend({ balanceAfterPaisa: paisa() })),
    truncated: z.boolean(),
});
// POS money events (published by the pos module, consumed by accounting) ----------------------------------------------
export const posCashMovementEventSchema = z.object({
    cashMovementId: z.uuid(),
    shiftId: z.uuid(),
    deviceId: z.uuid(),
    locationId: z.uuid(),
    registerId: z.uuid(),
    direction: z.enum(PAYMENT_DIRECTIONS),
    amountPaisa: paisa().positive(),
    reason: z.string(),
    note: z.string(),
    staffUserId: z.uuid(),
    occurredAt: z.iso.datetime(),
});
export const posShiftClosedEventSchema = z.object({
    shiftId: z.uuid(),
    deviceId: z.uuid(),
    locationId: z.uuid(),
    registerId: z.uuid(),
    businessDate: z.iso.date(),
    openingFloatPaisa: paisa(),
    expectedCashPaisa: paisa(),
    countedCashPaisa: paisa(),
    /** Counted − expected: positive over, negative short. */
    variancePaisa: paisa(),
    closedBy: z.uuid(),
    closedAt: z.iso.datetime(),
});
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "accounting" };
const viewExpenses = { permission: "expenses.view" };
const manageExpenses = { permission: "expenses.manage" };
const view = { permission: "accounting.view" };
export const listExpenseCategories = defineEndpoint({
    ...tag,
    name: "listExpenseCategories",
    method: "GET",
    path: "/v1/expense-categories",
    access: viewExpenses,
    summary: "Expense categories by name",
    status: 200,
    response: expenseCategoryListSchema,
});
export const createExpenseCategory = defineEndpoint({
    ...tag,
    name: "createExpenseCategory",
    method: "POST",
    path: "/v1/expense-categories",
    access: { permission: "expense_categories.manage" },
    summary: "Add an expense category",
    body: createExpenseCategoryRequestSchema,
    status: 201,
    response: expenseCategorySchema,
});
export const updateExpenseCategory = defineEndpoint({
    ...tag,
    name: "updateExpenseCategory",
    method: "PATCH",
    path: "/v1/expense-categories/{id}",
    access: { permission: "expense_categories.manage" },
    summary: "Rename or archive an expense category",
    params: idParamsSchema,
    body: updateExpenseCategoryRequestSchema,
    status: 200,
    response: expenseCategorySchema,
});
export const listExpenses = defineEndpoint({
    ...tag,
    name: "listExpenses",
    method: "GET",
    path: "/v1/expenses",
    access: viewExpenses,
    summary: "Expenses at the caller's locations, newest first",
    query: expenseListQuerySchema,
    status: 200,
    response: expenseListSchema,
});
export const getExpense = defineEndpoint({
    ...tag,
    name: "getExpense",
    method: "GET",
    path: "/v1/expenses/{id}",
    access: viewExpenses,
    summary: "One expense",
    params: idParamsSchema,
    status: 200,
    response: expenseSchema,
});
export const recordExpense = defineEndpoint({
    ...tag,
    name: "recordExpense",
    method: "POST",
    path: "/v1/expenses",
    access: manageExpenses,
    summary: "Record an expense paid from an account",
    body: recordExpenseRequestSchema,
    idempotent: true,
    status: 201,
    response: expenseSchema,
    errors: ["CATEGORY_ARCHIVED", "ACCOUNT_ARCHIVED"],
});
export const voidExpense = defineEndpoint({
    ...tag,
    name: "voidExpense",
    method: "POST",
    path: "/v1/expenses/{id}/void",
    access: manageExpenses,
    summary: "Void an expense with a reversing entry (the money goes back to the account)",
    params: idParamsSchema,
    body: voidExpenseRequestSchema,
    idempotent: true,
    status: 201,
    response: expenseSchema,
    errors: ["ALREADY_REVERSED"],
});
export const listFinancialTransactions = defineEndpoint({
    ...tag,
    name: "listFinancialTransactions",
    method: "GET",
    path: "/v1/financial-transactions",
    access: view,
    summary: "Every money movement (the journal), newest first",
    query: financialTransactionListQuerySchema,
    status: 200,
    response: financialTransactionListSchema,
});
export const getCashbook = defineEndpoint({
    ...tag,
    name: "getCashbook",
    method: "GET",
    path: "/v1/cashbook",
    access: view,
    summary: "A location's cash accounts: opening, every movement with its running balance, day-end balances",
    query: cashbookQuerySchema,
    status: 200,
    response: cashbookSchema,
});
//# sourceMappingURL=accounting.js.map